import axios from 'axios';

const BACKEND_URL = 'https://fotografx-backend-858320253284.europe-west1.run.app';
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      if (window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/admin/login', credentials),
  login2FA: (data) => api.post('/admin/login-2fa', data),
  verify: () => api.get('/admin/verify'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/admin/dashboard/stats'),
  getRecentActivity: () => api.get('/admin/dashboard/recent-activity'),
};

// Portfolio API
export const portfolioAPI = {
  getAll: (params = {}) => api.get('/admin/portfolio/', { params }),
  getById: (id) => api.get(`/admin/portfolio/${id}`),
  create: (formData) => api.post('/admin/portfolio/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/admin/portfolio/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/admin/portfolio/${id}`),
};

// Blog API
export const blogAPI = {
  getAll: (params = {}) => api.get('/admin/blog/', { params }),
  getById: (id) => api.get(`/admin/blog/${id}`),
  create: (formData) => api.post('/admin/blog/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/admin/blog/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/admin/blog/${id}`),
};

// Reviews API
export const reviewsAPI = {
  getAll: (params = {}) => api.get('/admin/reviews/', { params }),
  getById: (id) => api.get(`/admin/reviews/${id}`),
  create: (data) => api.post('/admin/reviews/', data),
  update: (id, data) => api.put(`/admin/reviews/${id}`, data),
  delete: (id) => api.delete(`/admin/reviews/${id}`),
};

// Forms API
export const formsAPI = {
  getContacts: (params = {}) => api.get('/admin/forms/contacts', { params }),
  getContact: (id) => api.get(`/admin/forms/contacts/${id}`),
  updateContact: (id, data) => api.put(`/admin/forms/contacts/${id}`, data),
  deleteContact: (id) => api.delete(`/admin/forms/contacts/${id}`),
  
  getBookings: (params = {}) => api.get('/admin/forms/bookings', { params }),
  getBooking: (id) => api.get(`/admin/forms/bookings/${id}`),
  updateBooking: (id, data) => api.put(`/admin/forms/bookings/${id}`, data),
  deleteBooking: (id) => api.delete(`/admin/forms/bookings/${id}`),
};

// Newsletter API
export const newsletterAPI = {
  getAll: (params = {}) => api.get('/admin/newsletter', { params }),
  getById: (id) => api.get(`/admin/newsletter/${id}`),
  update: (id, data) => api.put(`/admin/newsletter/${id}`, data),
  delete: (id) => api.delete(`/admin/newsletter/${id}`),
  getStats: () => api.get('/admin/newsletter/stats/summary'),
};

// Upload API
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/uploads/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  listImages: () => api.get('/admin/uploads/images'),
  deleteImage: (filename) => api.delete(`/admin/uploads/image/${filename}`),
};

// Settings API
export const settingsAPI = {
  getUserSettings: () => api.get('/admin/settings/user'),
  getProfile: () => api.get('/admin/settings/profile'),
  updateProfile: (data) => api.put('/admin/settings/profile', data),
  changePassword: (data) => api.put('/admin/settings/password', data),
  updateSocialLinks: (data) => api.put('/admin/settings/social', data),
  getImpressum: () => api.get('/admin/settings/impressum'),
  updateImpressum: (data) => api.put('/admin/settings/impressum', data),
  getSiteImages: () => api.get('/admin/settings/site-images'),
  updateSiteImages: (data) => api.put('/admin/settings/site-images', data),
  getAnalytics: () => api.get('/admin/settings/analytics'),
  updateAnalytics: (data) => api.put('/admin/settings/analytics', data),
  getContactSettings: () => api.get('/admin/settings/contact'),
  updateContactSettings: (data) => api.put('/admin/settings/contact', data),
  getServices: () => api.get('/public/services'),
  updateServices: (data) => api.put('/admin/settings/services', data),
  getTariffs: () => api.get('/public/tariffs'),
  updateTariffs: (data) => api.put('/admin/settings/tariffs', data),
  getSiteBranding: () => api.get('/admin/settings/branding'),
  updateSiteBranding: (data) => api.put('/admin/settings/branding', data),
  
  // Locations Management (Admin)
  createLocation: (locationData) => api.post('/admin/settings/locations', locationData),
  updateLocation: (locationId, locationData) => api.put(`/admin/settings/locations/${locationId}`, locationData),
  updateAllLocations: (locationsData) => api.put('/admin/settings/locations', locationsData),
  deleteLocation: (locationId) => api.delete(`/admin/settings/locations/${locationId}`),
  
  // Email notifications
  getEmailNotifications: () => api.get('/admin/settings/email-notifications'),
  updateEmailNotifications: (data) => api.put('/admin/settings/email-notifications', data),
  testEmail: () => api.post('/admin/settings/test-email'),
  getDocuments: () => api.get('/admin/settings/documents'),
  updateDocuments: (data) => api.put('/admin/settings/documents', data),
  createDocument: (data) => api.post('/admin/settings/documents', data),
  updateDocument: (id, data) => api.put(`/admin/settings/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/admin/settings/documents/${id}`)
};

// Portfolio Categories API  
export const portfolioCategoriesAPI = {
  getAll: () => api.get('/admin/portfolio-categories'),
  create: (data) => api.post('/admin/portfolio-categories', data),
  update: (id, data) => api.put(`/admin/portfolio-categories/${id}`, data),
  delete: (id) => api.delete(`/admin/portfolio-categories/${id}`)
};

// Tracking Codes API
export const trackingAPI = {
  getAll: (params = {}) => api.get('/admin/tracking', { params }),
  getById: (id) => api.get(`/admin/tracking/${id}`),
  create: (data) => api.post('/admin/tracking', data),
  update: (id, data) => api.put(`/admin/tracking/${id}`, data),
  delete: (id) => api.delete(`/admin/tracking/${id}`),
  getActive: () => api.get('/admin/tracking/public/active'),
};

// Legacy apiService object for compatibility
export const apiService = {
  // Tracking Codes
  getTrackingCodes: async () => {
    const response = await trackingAPI.getAll();
    return response.data;
  },
  createTrackingCode: async (data) => {
    const response = await trackingAPI.create(data);
    return response.data;
  },
  updateTrackingCode: async (id, data) => {
    const response = await trackingAPI.update(id, data);
    return response.data;
  },
  deleteTrackingCode: async (id) => {
    const response = await trackingAPI.delete(id);
    return response.data;
  },
  getActiveTrackingCodes: async () => {
    const response = await trackingAPI.getActive();
    return response.data;
  },
};

// Public API (for frontend)
export const publicAPI = {
  getPortfolio: (params = {}) => api.get('/public/portfolio', { params }),
  getPortfolioProject: (id) => api.get(`/public/portfolio/${id}`),
  getBlog: (params = {}) => api.get('/public/blog', { params }),
  getBlogPost: (id) => api.get(`/public/blog/${id}`),
  getReviews: (params = {}) => api.get('/public/reviews', { params }),
  submitContact: (data) => api.post('/public/contact', data),
  submitBooking: (data) => api.post('/public/booking', data),
  subscribeNewsletter: (data) => api.post('/public/newsletter', data),
  getSiteImages: () => api.get('/admin/settings/site-images'),
  getAnalytics: () => api.get('/admin/settings/analytics'),
  getContactSettings: () => api.get('/admin/settings/contact'),
  getServices: () => api.get('/public/services'),
  getTariffs: () => api.get('/public/tariffs'),
  getSiteBranding: () => api.get('/admin/settings/branding'),
  getPortfolioCategories: () => api.get('/portfolio-categories'),
  getDocuments: () => api.get('/admin/settings/documents'),
  
  // Locations Management  
  getLocations: () => api.get('/public/locations'), // Public endpoint for locations
};

// Utility functions
export const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${BACKEND_URL}${path}`;
};

export const isAuthenticated = () => {
  return !!localStorage.getItem('admin_token');
};

export const logout = () => {
  localStorage.removeItem('admin_token');
  window.location.href = '/admin/login';
};